import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';

import { useTranslation } from '../../../../i18n';

import { Layout, Input, Form, Button } from '../../../element';

import { actions as ratesActs } from '../../../../entities/rates';
import {
  getDeclarationFullPrice,
  getDeclarationFullWeight,
  getDeclarations,
} from '../../../../entities/rates/selectors';
import { getParcelWeight } from '../../../../entities/providers/selectors';

import { getRules } from './validation';

const DeclarationInfo: React.FC = () => {
  const [t] = useTranslation('common');

  const fullPrice = useSelector(getDeclarationFullPrice);
  const fullWeight = useSelector(getDeclarationFullWeight);
  const weight = useSelector(getParcelWeight);

  const isValid = useMemo(() => fullWeight === weight, [fullWeight, weight]);

  return (
    <>
      <Layout.Row gutter={20} type="flex" justify="end">
        <Layout.Col span={24} xs={24} sm={24} md={5} lg={5}>
          <Form.Item
            className={classNames({
              'is-error': !isValid,
            })}
          >
            <Input value={weight.toFixed(1)} disabled append={t('units.kg')} />
          </Form.Item>
        </Layout.Col>

        <Layout.Col span={24} xs={24} sm={24} md={9} lg={9}>
          <Form.Item>
            <Input value={fullPrice} disabled append="EUR" />
          </Form.Item>
        </Layout.Col>
      </Layout.Row>
    </>
  );
};

export const CustomsDeclaration: React.FC = () => {
  const [t, { language }] = useTranslation(['common', 'rates']);

  const dispatch = useDispatch();

  const declarations = useSelector(getDeclarations);
  const fullWeight = useSelector(getDeclarationFullWeight);

  const rules = useMemo(() => getRules(t), [language]);

  const handleFieldUpdate = useCallback(
    (key: string, idx: number, type: 'string' | 'number' = 'string') => (value: NotTyped) => {
      if (key === 'description') {
        const re = /^[a-zA-Z]+$/;

        const spaces = value.split(' ').length - 1;

        const validated =
          value.length === 0 || (re.test(value.split(' ').join('')) && value.length <= 20 && spaces === 0);

        if (!validated) {
          return;
        }
      }

      if (type === 'number') {
        value = String(value).replace(',', '.');

        if (value.includes('.')) {
          const [int, fractional] = value.split('.');

          if (fractional) {
            const fr = fractional.slice(0, 1);

            value = `${int}.${fr}`;
          }
        }
      }

      dispatch(
        ratesActs.updateDeclaration({
          key,
          idx,
          value,
        })
      );
    },
    []
  );

  const add = useCallback(() => {
    dispatch(ratesActs.addDeclaration());
  }, []);

  const remove = useCallback(
    (idx: number) => () => {
      dispatch(ratesActs.removeDeclaration({ position: idx }));
    },
    []
  );

  return (
    <>
      {declarations.map((declaration, idx) => (
        <Layout.Row key={idx.toString()} gutter={20}>
          <Layout.Col span={24} xs={24} sm={24} md={6} lg={6}>
            <Form.Item prop={`declarations:${idx}`} rules={rules.description}>
              <Input
                placeholder={t('rates:rates.declaration.description')}
                value={declaration.description}
                onChange={handleFieldUpdate('description', idx)}
              />
            </Form.Item>
          </Layout.Col>

          <Layout.Col span={24} xs={24} sm={24} md={4} lg={4}>
            <Form.Item prop={`declarations:${idx}`} rules={rules.amount}>
              <Input
                type="number"
                placeholder={t('rates:rates.declaration.amount')}
                min={1}
                value={declaration.count || ''}
                onChange={handleFieldUpdate('count', idx, 'number')}
              />
            </Form.Item>
          </Layout.Col>

          <Layout.Col span={24} xs={24} sm={24} md={5} lg={5}>
            <Form.Item prop={`declarations:${idx}`} rules={rules.weight}>
              <Input
                type="number"
                placeholder={t('rates:rates.declaration.weight')}
                min={1}
                value={declaration.weight || ''}
                onChange={handleFieldUpdate('weight', idx, 'number')}
              />
            </Form.Item>
          </Layout.Col>

          <Layout.Col span={24} xs={24} sm={24} md={6} lg={6}>
            <Form.Item prop={`declarations:${idx}`} rules={rules.price}>
              <Input
                type="number"
                placeholder={t('rates:rates.declaration.price')}
                min={0.1}
                value={declaration.price || ''}
                onChange={handleFieldUpdate('price', idx, 'number')}
              />
            </Form.Item>
          </Layout.Col>

          <Layout.Col span={24} xs={24} sm={24} md={3} lg={3}>
            <Form.Item>
              <Button
                nativeType="button"
                type="danger"
                plain
                icon="delete"
                className="full-width"
                disabled={declarations.length <= 1}
                onClick={remove(idx)}
              />
            </Form.Item>
          </Layout.Col>
        </Layout.Row>
      ))}

      <Layout.Row gutter={20} type="flex" justify="end">
        <Layout.Col span={24} xs={24} sm={24} md={15} lg={15}>
          <Form.Item>
            <div style={{ textAlign: 'center' }}>
              {t('rates:rates.declaration.total')}: {fullWeight.toFixed(1)} {t('units.kg')}
            </div>
          </Form.Item>
        </Layout.Col>

        <Layout.Col span={24} xs={24} sm={24} md={9} lg={9}>
          <Form.Item>
            <Button
              nativeType="button"
              type="info"
              icon="circle-plus-outline"
              className="full-width"
              onClick={add}
              disabled={declarations.length >= 8}
            >
              {t('rates:rates.declaration.add')}
            </Button>
          </Form.Item>
        </Layout.Col>
      </Layout.Row>

      <DeclarationInfo />
    </>
  );
};
